import { useId } from "react";
import { clsx } from "clsx";
import { SectionHeading } from "@/features/subventions/components/feed/SectionHeading";
import { useSubventionHistory } from "@/features/subventions/hooks/useSubventionHistory";
import type {
  EligibleSubsidy, OthersScope, SubsidyUsageEntry, UsageHistoryResponse,
} from "@/features/subventions/types/subvention.types";
import { reservationStatusBadge } from "@/features/subventions/utils/reservationStatus";
import {
  eur, formatDateShort, parseDate, qty, smallestCap,
} from "@/features/subventions/utils/subventionStatus";

type SubventionUsageHistoryProps = {
  subsidy: EligibleSubsidy
  holderId: string | number | null
  open: boolean
};

const HEADING = "Historique des utilisations";

// Who the "other members" are, by the shared pot's scope: the whole organisation for a global
// pot, the household for a per_employee one.
const OTHERS_AUDIENCE: Record<OthersScope, string> = {
  global: "d'autres membres de l'organisation",
  per_employee: "d'autres membres du foyer",
};

// Guard the bare usage-entry date through parseDate (as expiration_date is), so an unparseable
// value degrades to its raw string instead of rendering "Invalid Date".
const formatDay = (iso: string): string => {
  const date = parseDate(iso);
  return date ? formatDateShort(date) : iso;
};

type UsageHistoryTableProps = {
  entries: SubsidyUsageEntry[]
  cap: ReturnType<typeof smallestCap>
  inQuantity: boolean
  headingId: string
};

// An order/refund still "en cours de paiement": the same amber pill the feed already used, now
// expressed in the shared badge shape so a reservation status and a pending order render alike.
const PENDING_ORDER_BADGE = {
  label: "en cours",
  classes: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const UsageHistoryTable = ({
  entries, cap, inQuantity, headingId,
}: UsageHistoryTableProps) => {
  const format = inQuantity ? qty : eur;
  // Round the float sum back to centimes so accumulated IEEE-754 drift can't render a clean
  // whole-euro total as "10,00 €" instead of "10 €".
  const total = Math.round(entries.reduce((sum, entry) => sum + (inQuantity ? entry.quantity : entry.amount), 0) * 100) / 100;
  const capText = cap ? `${cap.money ? eur(cap.value) : qty(cap.value)} ${cap.periodFr}` : null;

  return (
    <table aria-labelledby={headingId} className="w-full border-collapse text-[13px]">
      <thead>
        <tr className="text-[10.5px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          <th className="pb-2 text-left font-bold">Date</th>
          <th className="pb-2 text-left font-bold">Opération</th>
          <th className="pb-2 text-right font-bold">{inQuantity ? "Quantité" : "Montant"}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => {
          // A rental reservation shows its booking status (Yeati-Loc); an order/refund keeps the
          // generic "en cours" pending flag. Both collapse to a single optional badge.
          const badge = reservationStatusBadge(entry.reservation_status) ?? (entry.pending ? PENDING_ORDER_BADGE : null);
          const srPrefix = badge ? `${badge.label}, ` : "débité ";

          return (
            <tr key={entry.id} className="border-t border-gray-100 dark:border-gray-800">
              <td className="py-2.5 text-gray-400 dark:text-gray-500">{formatDay(entry.date)}</td>
              <td className="py-2.5 text-gray-700 dark:text-gray-200">
                {entry.label}
                {badge && (
                  <span className={clsx("ml-2 inline-block rounded-full px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide", badge.classes)}>
                    {badge.label}
                  </span>
                )}
              </td>
              <td
                className={clsx(
                  "py-2.5 text-right font-semibold tabular-nums",
                  // A pending hold reads amber (matching its badge), not the settled-debit red,
                  // so the amount column alone distinguishes provisional from final.
                  entry.pending ? "text-amber-600 dark:text-amber-400" : "text-error-emphasis",
                )}
              >
                <span className="sr-only">{srPrefix}</span>
                {`−${format(inQuantity ? entry.quantity : entry.amount)}`}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="border-t-2 border-gray-200 font-bold dark:border-gray-700">
          <td className="pt-2.5 text-gray-500 dark:text-gray-400" colSpan={2}>Total utilisé</td>
          <td className="pt-2.5 text-right tabular-nums text-gray-900 dark:text-white">
            {format(total)}
            {capText && <span className="font-medium text-gray-400 dark:text-gray-500">{` / ${capText}`}</span>}
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

UsageHistoryTable.displayName = "UsageHistoryTable";

type HistoryView = {
  entries: SubsidyUsageEntry[]
  cap: ReturnType<typeof smallestCap>
  inQuantity: boolean
  othersNote: string | null
};

const buildHistoryView = (subsidy: EligibleSubsidy, data: UsageHistoryResponse | undefined): HistoryView => {
  const entries = data?.entries ?? [];
  const cap = smallestCap(subsidy);
  // When the binding cap is a quantity (not money), the perimeter is spent in articles, so the
  // history reads quantities, not euros, otherwise a quantity-only advantage shows empty 0,00 €.
  const inQuantity = cap !== null && !cap.money;
  // Spend by others on a shared pot, in euros. Only surfaced on money-bound perimeters, where it
  // explains a remaining figure the viewer's own rows alone do not.
  const othersAmount = inQuantity ? null : data?.others_amount ?? null;
  const scope = data?.others_scope ?? null;
  // The reconciliation sentence, named by who the others are (organisation vs household). Built
  // here so the render stays a plain conditional.
  const othersNote = othersAmount !== null && othersAmount > 0 && scope
    ? `${eur(othersAmount)} ont été utilisés par ${OTHERS_AUDIENCE[scope]} sur ce budget partagé.`
    : null;
  return {
    entries, cap, inQuantity, othersNote,
  };
};

export const SubventionUsageHistory = ({ subsidy, holderId, open }: SubventionUsageHistoryProps) => {
  const headingId = useId();
  const { data, isLoading, isError } = useSubventionHistory(subsidy.subsidy_perimeter_id, holderId, open);

  // While the lazy fetch is in flight, render nothing rather than a skeleton. Most perimeters have
  // no history, so a skeleton that then collapses to null on an empty result reads as a glitch as
  // the drawer slides in. The table fades in once there are actually entries to show.
  if (isLoading) return null;

  const {
    entries, cap, inQuantity, othersNote,
  } = buildHistoryView(subsidy, data);

  // "When applicable": hide the section on error, or when there is neither own history nor a
  // shared-spend figure to reconcile. A shared pot the viewer never touched still shows the line.
  if (isError || (!entries.length && !othersNote)) return null;

  return (
    <section className="mt-5 animate-in fade-in duration-200 motion-reduce:animate-none">
      <SectionHeading id={headingId}>{HEADING}</SectionHeading>
      {entries.length > 0 && (
        <UsageHistoryTable entries={entries} cap={cap} inQuantity={inQuantity} headingId={headingId} />
      )}
      {data?.truncated && (
        <p className="mt-2.5 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
          {`Les ${data.limit} utilisations les plus récentes sont affichées.`}
        </p>
      )}
      {othersNote && (
        <p className="mt-2.5 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
          {othersNote}
        </p>
      )}
    </section>
  );
};

SubventionUsageHistory.displayName = "SubventionUsageHistory";
